import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

import { updateTraderContactAPI } from 'api/corporate-contacts'
import {
  CorporateContactFormTypes,
  TraderContactDataTypes,
  UpdateTraderContactTypes,
} from 'types/corporateContacts'

const INITIAL_VALUES: CorporateContactFormTypes = {
  traderId: 0,
  contactId: 0,
  titleId: 1,
  firstName: '',
  lastName: '',
  middleNames: '',
  phone1: '',
  phone2: '',
  fax1: '',
  fax2: '',
  email: '',
  mobile: '',
  comments: '',
  isPrimary: false,
  isActive: true,
}

export const useUpdateCorporateContact = (
  selectedCorporateContact: TraderContactDataTypes,
  onCloseModal: () => void
) => {
  const { traderId } = useParams()
  const queryClient = useQueryClient()
  const corporateTraderId = Number(traderId) || 0

  const [initialValues] = useState<CorporateContactFormTypes>({
    ...INITIAL_VALUES,
    traderId: corporateTraderId,
    contactId: selectedCorporateContact.contactId || 0,
    titleId: selectedCorporateContact.titleId ?? 1,
    firstName: selectedCorporateContact.firstName || '',
    lastName: selectedCorporateContact.lastName || '',
    middleNames: selectedCorporateContact.middleNames || '',
    phone1: selectedCorporateContact.phone1 || '',
    phone2: selectedCorporateContact.phone2 || '',
    fax1: selectedCorporateContact.fax1 || '',
    fax2: selectedCorporateContact.fax2 || '',
    email: selectedCorporateContact.email || '',
    mobile: selectedCorporateContact.mobile || '',
    comments: selectedCorporateContact.comments || '',
    isPrimary: Boolean(selectedCorporateContact.isPrimary),
    isActive: Boolean(selectedCorporateContact.isActive),
  })

  const isLoading = useMemo(() => false, [])

  const handleSubmitUpdateCorporateContact = useCallback(
    async (values: CorporateContactFormTypes) => {
      const payload: UpdateTraderContactTypes = {
        contactId: values.contactId || 0,
        traderId: corporateTraderId,
        titleId: values.titleId,
        firstName: values.firstName,
        lastName: values.lastName,
        middleNames: values.middleNames,
        phone1: values.phone1,
        phone2: values.phone2,
        fax1: values.fax1,
        fax2: values.fax2,
        email: values.email,
        mobile: values.mobile,
        comments: values.comments,
        isPrimary: values.isPrimary,
        isActive: values.isActive,
      }

      try {
        await updateTraderContactAPI(payload)
        toast.success('Contact updated successfully.')
        queryClient.invalidateQueries({ queryKey: ['useCorporateContacts'] })
        onCloseModal()
      } catch (error) {
        const message = JSON.parse(JSON.stringify(error))
        toast.error(message.outcomeUserMessage || 'Failed to update contact.')
      }
    },
    [corporateTraderId, onCloseModal, queryClient]
  )

  return {
    initialValues,
    isLoading,
    handleSubmitUpdateCorporateContact,
  }
}
